import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridCheckCircleIcon, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Stack, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Table, useTableState } from 'modules/ui/table';
import { Loader } from 'modules/ui/loader';
import { useModal } from 'modules/ui/modal';
import { dateConfig } from 'modules/app/constants';
import { useMessages } from 'modules/messages/hooks/useMessages';
import { PaymentStatusEnum } from 'modules/financial/interfaces';
import { useAuth } from 'modules/auth/hooks/useAuth';

import { getAllUnassignedCalls, callCheckout } from '../hooks/crud';
import { UnassignedCallDetailsModal } from './UnassignedCallDetailsModal';

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { ExternalCallData } from '../interfaces';
import { useIsMobile } from 'modules/app/hooks/useIsMobile';

import { useHistory } from 'react-router-dom';

export const UnassignedCallList = () => {
  const isMobile = useIsMobile();
  const history = useHistory();

  const location = useMemo(() => window.location.origin, []);

  const { t } = useTranslation('common');
  const { isAllAdmin } = useAuth();
  const { show } = useMessages();
  const [currentRowId, setCurrentRowId] = useState<number | null>(null);
  const [callDetails, setCallDetails] = useState<ExternalCallData>();
  const { isOpen, close, showModal } = useModal();
  const [tableData, setTableData] = useState<ExternalCallData[]>([]);

  const [tableState, onChangeTableState] = useTableState({
    orderBy: [{ direction: 'desc', field: 'created_at' }],
  });

  const {
    data: calls,
    isLoading,
    error,
    isError,
    refetch,
  } = useQuery<ExternalCallData[]>(['allUnassignedCalls'], getAllUnassignedCalls, {
    refetchInterval: 10000,
  });

  useEffect(() => {
    if (calls) setTableData(calls);
    if (isError && error) {
      show({ message: `${t('general.messages.failed')}. ${error}` });
    }
  }, [calls, error, isError, show, t]);

  const { mutate: acceptCall, isLoading: isLoadingAccept } = useMutation(callCheckout, {
    onSuccess: (call, id) => {
      setTableData(prev => prev.filter(row => row.id !== id));
      show({ message: t('general.messages.success'), severity: 'success' });
      setCurrentRowId(null);

      const token = call?.token?.technician_token;
      if (token) {
        history.push(`/room/${token}`);
      }
    },
    onError: ({ message }) => {
      show({ message: `${t('general.messages.failed')}. ${message}` });
      setCurrentRowId(null);
    },
  });

  const handleAccept = useCallback(
    (id: number) => (event: React.MouseEvent) => {
      event.stopPropagation();
      setCurrentRowId(id);
      acceptCall(id);
    },
    [acceptCall],
  );

  const handleRowClick = (params: ExternalCallData) => {
    setCallDetails(params);
    showModal();
  };

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'client_name',
        headerName: t('call.list.customerName'),
        valueGetter: ({ value }) => value || '-',
        flex: 1,
        minWidth: 165,
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: GridRenderCellParams<ExternalCallData>) => {
          const isCurrent = currentRowId === row.id;
  
          return (
            <LoadingButton
              loading={isLoadingAccept && isCurrent}
              loadingPosition="start"
              onClick={row.id ? handleAccept(row.id) : undefined}
              variant="contained"
              color="secondary"
              sx={{
                justifyContent: 'flex-start',
                mr: { md: 4 },
                mt: { xs: 1, md: 0 },
                mb: { xs: 1, md: 0 },
              }}
              startIcon={<GridCheckCircleIcon />}
            >
              {t('call.button.accept')}
            </LoadingButton>
          );
        },
        width: 175,
        minWidth: 175,
      },
    ];
  
    if (!isMobile) {
      baseColumns.splice(1, 0, // insert after client_name
        {
          field: 'client_phone_number',
          headerName: t('call.list.customerPhone'),
          valueGetter: ({ value }) => value || '-',
          flex: 1,
          minWidth: 150,
        },
        {
          field: 'created_at',
          headerName: t('call.list.callCreate'),
          valueFormatter: ({ value }) => value ? dayjs(value).format(dateConfig.format.dateTime) : '-',
          flex: 1,
          minWidth: 130,
        },
        {
          field: 'call_description',
          headerName: t('call.list.description'),
          valueGetter: ({ value }) => value || '-',
          flex: 1,
          minWidth: 420,
        }
      );
    }
  
    return baseColumns;
  }, [t, currentRowId, isLoadingAccept, handleAccept, isMobile]);  

  return (
    <>
      <Loader isLoading={isLoading} />

      <Table
        loading={isLoading}
        columns={columns}
        rows={tableData}
        rowCount={tableData.length}
        state={tableState}
        onChangeState={onChangeTableState}
        onRowClick={isMobile ? ({ row }) => handleRowClick(row) : undefined}
      />

      {callDetails && <UnassignedCallDetailsModal open={isOpen} onClose={close} call={callDetails} />}
    </>
  );
};
